import React, {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useRef,
  useEffect,
  useState,
} from "react";
import { VersionedDataContext } from "../versioned_data/context";

type RowEditState = {
  committedValues: Record<string, string>;
  originalValues: Record<string, string>;
  approved?: boolean; // Internal field - not saved to S3, tracks approval status
};

// Combined context that includes both the unsaved guard and edits functionality
export const SupplyWalkEditsContext = createContext<{
  hasUnapprovedEdits: () => boolean;
  hasAnyApprovedRows: () => boolean;
  setApprovedRowCount: (count: number) => void;
  incrementApprovedRowCount: () => void;
  decrementApprovedRowCount: () => void;
  discardChanges: () => void;
  setRowApproved: (rowKey: string, approved: boolean) => void;
  removeRowEdit: (rowKey: string) => void;
  storeEdit: (rowKey: string, editState: RowEditState) => void;
  getEdit: (rowKey: string) => RowEditState | undefined;
  reloadVersion: number;
  bumpReloadVersion: () => void;
  // Optimistic per-row delta: rowKey → status the row was in when approved.
  // Lets the status-bar tab counts decrement the source bucket immediately
  // (and bump Approved) without waiting for the save → server-refresh round
  // trip. Cleared by the consumer when fresh parsedData lands.
  pendingApprovalDeltas: Map<string, string>;
  recordPendingApproval: (rowKey: string, sourceStatus: string) => void;
  clearPendingApproval: (rowKey: string) => void;
  clearAllPendingApprovals: () => void;
} | null>(null);

export const useSupplyWalkEdits = () => {
  const ctx = useContext(SupplyWalkEditsContext);
  return ctx;
};

// Global provider for supply walk edits
export const SupplyWalkEditsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // Global storage for unapproved edits (shared across all instances)
  const globalEditStorage = useRef<Map<string, RowEditState>>(new Map());

  // Track approved row count from table data
  const approvedRowCountRef = useRef<number>(0);

  // Bumped after a successful saveApprovedPOs so consumers can force a
  // re-subscription that picks up freshly-invalidated transform data.
  const [reloadVersion, setReloadVersion] = useState(0);
  const bumpReloadVersion = useCallback(() => {
    setReloadVersion((v) => v + 1);
  }, []);

  // Optimistic deltas keyed by rowKey → original bucket status (e.g. the
  // GoodChop "Existing PO's to Modify" string). Used by the status bar so
  // the tab counts decrement the source bucket and bump Approved instantly
  // on click instead of waiting for the save round-trip + re-subscription.
  const [pendingApprovalDeltas, setPendingApprovalDeltas] = useState<
    Map<string, string>
  >(new Map());

  const recordPendingApproval = useCallback(
    (rowKey: string, sourceStatus: string) => {
      setPendingApprovalDeltas((prev) => {
        if (prev.get(rowKey) === sourceStatus) return prev;
        const next = new Map(prev);
        next.set(rowKey, sourceStatus);
        return next;
      });
    },
    []
  );

  const clearPendingApproval = useCallback((rowKey: string) => {
    setPendingApprovalDeltas((prev) => {
      if (!prev.has(rowKey)) return prev;
      const next = new Map(prev);
      next.delete(rowKey);
      return next;
    });
  }, []);

  const clearAllPendingApprovals = useCallback(() => {
    setPendingApprovalDeltas((prev) => (prev.size === 0 ? prev : new Map()));
  }, []);

  // Get the main run from context to detect run changes
  const { mainRun } = useContext(VersionedDataContext);

  // Note: Storage is now run-specific due to runAttemptId in keys, so no need to clear on run change
  // The keys automatically isolate edits by run: `${poId}-${sku}-${location}-${supplier}-${runAttemptId}`
  // But we still need to dispatch events to notify other components
  useEffect(() => {
    // Dispatch event to trigger cache invalidation in other components
    window.dispatchEvent(new CustomEvent("runChanged"));
  }, [mainRun]);

  const hasUnapprovedEdits = useCallback(() => {
    if (!globalEditStorage.current) return false;

    for (const [, editState] of globalEditStorage.current) {
      if (editState.approved !== true) {
        return true;
      }
    }
    return false;
  }, []);

  const hasAnyApprovedRows = useCallback(() => {
    return approvedRowCountRef.current > 0;
  }, []);

  const setApprovedRowCount = useCallback((count: number) => {
    approvedRowCountRef.current = count;
    // Dispatch event to trigger guard recompute
    window.dispatchEvent(new CustomEvent("supplyEditsChanged"));
  }, []);

  const incrementApprovedRowCount = useCallback(() => {
    approvedRowCountRef.current += 1;
    // Dispatch event to trigger guard recompute
    window.dispatchEvent(new CustomEvent("supplyEditsChanged"));
  }, []);

  const decrementApprovedRowCount = useCallback(() => {
    approvedRowCountRef.current = Math.max(0, approvedRowCountRef.current - 1);
    // Dispatch event to trigger guard recompute
    window.dispatchEvent(new CustomEvent("supplyEditsChanged"));
  }, []);

  const storeEdit = useCallback((rowKey: string, editState: RowEditState) => {
    globalEditStorage.current.set(rowKey, editState);
    // Dispatch event to trigger guard recompute
    window.dispatchEvent(new CustomEvent("supplyEditsChanged"));
  }, []);

  const getEdit = useCallback((rowKey: string) => {
    return globalEditStorage.current.get(rowKey);
  }, []);

  const setRowApproved = useCallback((rowKey: string, approved: boolean) => {
    const editState = globalEditStorage.current.get(rowKey);
    if (editState) {
      editState.approved = approved;
    } else {
      // If editState doesn't exist, create a minimal one to track approval status
      // This ensures we can track approval even when there are no pending edits
      globalEditStorage.current.set(rowKey, {
        committedValues: {},
        originalValues: {},
        approved: approved,
      });
    }
    // Dispatch event to trigger guard recompute and notify nested details
    window.dispatchEvent(new CustomEvent("supplyEditsChanged"));
    // Dispatch approval toggle event for nested details component
    try {
      window.dispatchEvent(
        new CustomEvent("poApprovalToggled", {
          detail: { rowKey, checked: approved },
        })
      );
    } catch (_) {}
  }, []);

  const removeRowEdit = useCallback((rowKey: string) => {
    globalEditStorage.current.delete(rowKey);
    // Dispatch event to trigger guard recompute
    window.dispatchEvent(new CustomEvent("supplyEditsChanged"));
  }, []);

  const discardChanges = useCallback(() => {
    globalEditStorage.current.clear();
    // Dispatch event to trigger guard recompute
    window.dispatchEvent(new CustomEvent("supplyEditsChanged"));
  }, []);

  const value = useMemo(
    () => ({
      hasUnapprovedEdits,
      hasAnyApprovedRows,
      setApprovedRowCount,
      incrementApprovedRowCount,
      decrementApprovedRowCount,
      discardChanges,
      setRowApproved,
      removeRowEdit,
      storeEdit,
      getEdit,
      reloadVersion,
      bumpReloadVersion,
      pendingApprovalDeltas,
      recordPendingApproval,
      clearPendingApproval,
      clearAllPendingApprovals,
    }),
    [
      hasUnapprovedEdits,
      hasAnyApprovedRows,
      setApprovedRowCount,
      incrementApprovedRowCount,
      decrementApprovedRowCount,
      discardChanges,
      setRowApproved,
      removeRowEdit,
      storeEdit,
      getEdit,
      reloadVersion,
      bumpReloadVersion,
      pendingApprovalDeltas,
      recordPendingApproval,
      clearPendingApproval,
      clearAllPendingApprovals,
    ]
  );
  return (
    <SupplyWalkEditsContext.Provider value={value}>
      {children}
    </SupplyWalkEditsContext.Provider>
  );
};
